// v2 crow — a sleek glider seen on the wing: a long fuselage body with a
// blue-charcoal sheen mantle over the back (the glossy-black highlight), a round
// head with a heavy straight beak, broad two-panel wings — flat inner panels
// with swept outer hands ending in two fingered primary feathers — and a long
// fanned tail. Two plumages: glossy carrion crow and the grey-bodied,
// black-hooded hooded crow. A flyer: the body floats just above y = 0. Ported
// 1:1 from age-of-mcp's v2/crow.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const PLUMAGES = [
  { body: "#16161c", sheen: "#2e3444", head: "#1b1b22", wing: "#101016", beak: "#26262e" },
  { body: "#9a9aa0", sheen: "#b2b2b8", head: "#15151c", wing: "#121218", beak: "#22222a" },
];

function crowParts(v: number): Part[] {
  const c = PLUMAGES[v % PLUMAGES.length]!;
  const parts: Part[] = [
    // fuselage body
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.body,
      position: [0.002, 0.03, 0], scale: [1.6, 0.95, 0.85] },
    // glossy sheen mantle over the back
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.sheen,
      position: [0.004, 0.044, 0], scale: [1.35, 0.55, 0.95] },
    // round head with a heavy straight beak
    { geometry: new THREE.IcosahedronGeometry(0.0205, 0), color: c.head,
      position: [0.052, 0.043, 0] },
    { geometry: new THREE.ConeGeometry(0.008, 0.03, 4), color: c.beak,
      position: [0.078, 0.041, 0], rotation: [0, 0, -Math.PI / 2] },
    // dark eyes, barely catching the light
    { geometry: new THREE.IcosahedronGeometry(0.0026, 0), color: "#50545e",
      position: [0.06, 0.049, 0.0125] },
    { geometry: new THREE.IcosahedronGeometry(0.0026, 0), color: "#50545e",
      position: [0.06, 0.049, -0.0125] },
    // long fanned tail, tipped slightly down
    { geometry: new THREE.BoxGeometry(0.052, 0.005, 0.03), color: c.wing,
      position: [-0.06, 0.032, 0], rotation: [0, 0, 0.18] },
  ];
  // Broad gliding wings, one per side: a flat inner panel, a swept outer
  // hand, and two fingered primaries trailing off the tip.
  for (const s of [1, -1]) {
    parts.push(
      { geometry: new THREE.BoxGeometry(0.055, 0.006, 0.055), color: c.wing,
        position: [0.0, 0.038, 0.048 * s], rotation: [-0.12 * s, -0.18 * s, 0] },
      { geometry: new THREE.BoxGeometry(0.042, 0.005, 0.046), color: c.wing,
        position: [-0.014, 0.046, 0.088 * s], rotation: [-0.18 * s, -0.45 * s, 0] },
      { geometry: new THREE.BoxGeometry(0.03, 0.004, 0.0095), color: c.wing,
        position: [-0.032, 0.05, 0.112 * s], rotation: [-0.2 * s, -0.75 * s, 0] },
      { geometry: new THREE.BoxGeometry(0.028, 0.004, 0.009), color: c.wing,
        position: [-0.025, 0.049, 0.118 * s], rotation: [-0.2 * s, -0.55 * s, 0] },
    );
  }
  return parts;
}

export const crowV2 = defineModel({
  id: "crow-v2",
  batchId: "fable",
  name: "Crow (v2)",
  tags: ["creature","crow","low-poly","age-of-mcp","fable"],
  summary: "Low-poly crow.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.08,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => crowParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => crowParts(1) },
  ],
  animations: [],
});
